import React, { useState } from "react";
import Papa from "papaparse";

const sampleCSV = `First name,Last name,Ethnicity,Gender,Date of birth,Email,Phone number\nJane,Doe,Asian,Female,1990-01-01,jane.doe@email.com,1234567890\nJohn,Smith,Hispanic,Male,1985-05-12,john.smith@email.com,0987654321`;

const columns = [
  "First name",
  "Last name",
  "Ethnicity",
  "Gender",
  "Date of birth",
  "Email",
  "Phone number",
];

export default function CommaPatientImport({ doctorId, onImportComplete }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [patients, setPatients] = useState([]);
  const [csvError, setCsvError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleFileUpload = (e) => {
    setCsvError("");
    const file = e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        // Validate columns
        const missing = columns.filter(
          (col) => !results.meta.fields.includes(col),
        );
        if (missing.length > 0) {
          setCsvError(
            `Missing columns: ${missing.join(", ")}. Please use the correct template.`,
          );
          setPatients([]);
          return;
        }
        setPatients(results.data);
      },
      error: (err) => setCsvError("Failed to parse CSV: " + err.message),
    });
  };

  const handleEdit = (idx, field, value) => {
    setPatients((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, [field]: value } : row)),
    );
  };

  const handleRemove = (idx) => {
    setPatients((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setCsvError("");
    try {
      // Map Gender values before sending
      const genderMap = {
        Male: "M",
        Female: "F",
        Other: "Other",
        M: "M",
        F: "F",
      };
      const mappedPatients = patients.map((p) => ({
        ...p,
        Gender: genderMap[p.Gender?.trim()] || "Other",
      }));
      // You may need to adjust the API endpoint and payload as per your backend
      const token = localStorage.getItem("authToken");
      const res = await fetch("https://genz-staging.feeltiptop.com/api/patient/bulk-import", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          doctorId,
          patients: mappedPatients,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setModalOpen(false);
        setPatients([]);
        if (onImportComplete) onImportComplete();
      } else {
        setCsvError(data.message || "Failed to import patients.");
      }
    } catch (err) {
      setCsvError("Failed to import patients: " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
        onClick={() => setModalOpen(true)}
      >
        Bulk Import
      </button>
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="relative w-full max-w-3xl rounded-lg bg-white p-8 shadow-lg">
            <button
              className="absolute right-2 top-2 text-gray-400 hover:text-gray-700"
              onClick={() => setModalOpen(false)}
            >
              &times;
            </button>
            <h2 className="mb-4 text-2xl font-bold">Bulk Import Patients</h2>
            <p className="mb-2 text-gray-700">
              Please upload a CSV file with columns:
              <span className="font-semibold">
                {" "}
                First name, Last name, Ethnicity, Gender, Date of birth, Email,
                and Phone number
              </span>
              .
            </p>
            <div className="mb-4">
              <span className="font-semibold">Example CSV:</span>
              <div className="mt-2 overflow-x-auto rounded border bg-gray-50">
                <table className="min-w-full text-left text-xs">
                  <thead className="bg-gray-100">
                    <tr>
                      {columns.map((col) => (
                        <th
                          key={col}
                          className="border-b px-2 py-1 font-semibold"
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Papa.parse(sampleCSV, { header: true }).data.map(
                      (row, i) => (
                        <tr key={i} className="border-b">
                          {columns.map((col) => (
                            <td key={col} className="px-2 py-1">
                              {row[col]}
                            </td>
                          ))}
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <input
              type="file"
              accept=".csv"
              className="mb-4"
              onChange={handleFileUpload}
            />
            {csvError && <div className="mb-2 text-red-600">{csvError}</div>}
            {patients.length > 0 && (
              <div className="mb-4">
                <h3 className="mb-2 text-lg font-semibold">Preview Patients</h3>
                <div className="overflow-x-auto rounded border bg-gray-50">
                  <table className="min-w-full text-left text-xs">
                    <thead className="bg-gray-100">
                      <tr>
                        {columns.map((col) => (
                          <th
                            key={col}
                            className="border-b px-2 py-1 font-semibold"
                          >
                            {col}
                          </th>
                        ))}
                        <th className="border-b px-2 py-1 font-semibold">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {patients.map((row, i) => (
                        <tr key={i} className="border-b">
                          {columns.map((col) => (
                            <td key={col} className="px-2 py-1">
                              <input
                                className="w-full rounded border px-1 py-0.5 text-xs"
                                value={row[col] || ""}
                                onChange={(e) =>
                                  handleEdit(i, col, e.target.value)
                                }
                              />
                            </td>
                          ))}
                          <td className="px-2 py-1">
                            <button
                              className="text-red-600 hover:underline"
                              onClick={() => handleRemove(i)}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button
                  className="mt-4 rounded bg-green-600 px-4 py-2 text-white hover:bg-green-700 disabled:opacity-50"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? "Importing..." : "Submit All"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
